import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {isProdMode} from "../../constants/mode";
import {getUrl} from "../../constants/urls";
import {usePageURLContext} from "../../contexts/pageURLContext";
import {requestInventories} from "../../redux/Actions/inventory.actions";
import {getCurrentCustomer} from "../../redux/Selectors/customers.selectors";
import {
  getInventories,
  getInventoryTotalCount,
  getUserPaginationUrls,
} from "../../redux/Selectors/inventories.selectors";
import {getCurrentSite} from "../../redux/Selectors/sites.selectors";
import {generateQueryUrl} from "../../helpers/generateQueryUrl";

export const useGetInventoryListURL = () => {
  const currentCustomer = useSelector(getCurrentCustomer);
  const currentSite = useSelector(getCurrentSite);

  const inventoryListURL = useMemo(
    () => getUrl("inventories_new_list")(currentCustomer.id, currentSite?.id),
    [currentCustomer.id, currentSite?.id]
  );

  return {inventoryListURL};
};

export const useFetchInventories = () => {
  const {pageUrl} = usePageURLContext();
  const dispatch = useDispatch();

  const freshFetch = (trackThisPromise) => {
    dispatch(requestInventories("", trackThisPromise));
  };

  const refetch = (trackThisPromise) => {
    dispatch(requestInventories(pageUrl, trackThisPromise));
  };

  return {refetch, freshFetch};
};

export const useInventoryData = () => {
  const dispatch = useDispatch();
  const {freshFetch, refetch} = useFetchInventories();
  const {inventoryListURL} = useGetInventoryListURL();
  const {setPageUrl} = usePageURLContext();
  const intervalRef = useRef();
  const currentSite = useSelector(getCurrentSite);
  const inventories = useSelector(getInventories);
  const totalInventories = useSelector(getInventoryTotalCount);
  const inventoryUrls = useSelector(getUserPaginationUrls);
  const [currentPage, setCurrentPage] = useState(1);

  const clearTimeInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  useEffect(() => {
    clearTimeInterval();
    freshFetch(true);
    if (isProdMode) {
      intervalRef.current = setInterval(() => refetch(false), 20000);
    }
    return clearTimeInterval;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSite.id]);

  const handlePageChange = (page) => {
    const url = currentPage < page ? inventoryUrls?.nextUrl : inventoryUrls?.prevUrl;
    setPageUrl(url);
    setCurrentPage(page);
    dispatch(requestInventories(url, true));
  };

  const paginationConfig = {
    currentPage,
    onPageChange: handlePageChange,
  };

  const handleSearch = (tokens) => {
    if (tokens?.length > 0) {
      const searchUrl = generateQueryUrl(inventoryListURL, tokens);
      dispatch(requestInventories(searchUrl, true));
      setPageUrl(searchUrl);
      setCurrentPage(1);
    } else {
      freshFetch(true);
      setPageUrl("");
      setCurrentPage(1);
    }
  };

  return {handleSearch, inventories, totalInventories, paginationConfig};
};
